"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";

import {
  ActionButtons,
  type ActionButtonsProps,
} from "../components/actionButtons";
import { cn } from "../lib/utils";
import { Icon } from "./icon";

interface DialogProps
  extends
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>,
    DialogContentProps {
  actionButtonProps?: ActionButtonsProps["actionButtonProps"];
  cancelButtonProps?: ActionButtonsProps["cancelButtonProps"];
  hasCancelButton?: ActionButtonsProps["hasCancelButton"];
  hasActionButton?: ActionButtonsProps["hasActionButton"];

  description?: string;

  /**
   * A `heading` value is required to show in screen readers for
   * accessibility. You can hide it if not needed by setting
   * `showHeading={false}`.
   */
  heading: React.ReactNode;

  /**
   * This will still output the heading HTML, but only for screen readers.
   */
  showHeading?: boolean;
}

/**
 * Radix UI (API reference): https://www.radix-ui.com/docs/primitives/components/dialog#api-reference
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/dialog
 *
 * The 'Cancel' button is shown by default. It can be hidden by
 * passing `hasCancelButton={false}`.
 *
 * The 'Submit' button is only shown if `actionButtonProps` is
 * defined. It can be hidden by passing `hasActionButton={false}`.
 */
const Dialog = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogProps
>(
  (
    {
      children,
      defaultOpen,
      description,
      heading,
      onOpenChange,
      open,
      showHeading = true,

      actionButtonProps,
      cancelButtonProps,
      hasCancelButton = true,
      hasActionButton = true,
      ...props
    },
    ref,
  ) => (
    <DialogPrimitive.Root
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent
        aria-describedby={description ?? undefined}
        ref={ref}
        {...props}
      >
        <DialogHeader
          className={cn({
            // Only show on screen readers if there are no children,
            // otherwise parent's `gap` will create whitespace.
            "sr-only": !showHeading && !description,
          })}
        >
          <DialogTitle className={cn(!showHeading && "sr-only")}>
            {heading}
          </DialogTitle>

          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {children}

        {(hasCancelButton || hasActionButton) && (
          <DialogFooter>
            <ActionButtons
              // Provide at least an empty object to ensure these buttons are always displayed.
              // This requires them to be hidden explicitly to enforce a Cancel / Accept
              // button pattern, i.e.
              // actionButtonProps={actionButtonProps || {}}
              actionButtonProps={actionButtonProps || {}}
              // Provide at least an empty object to ensure these buttons are always displayed.
              // This requires them to be hidden explicitly to enforce a Cancel / Accept
              // button pattern, i.e.
              // cancelButtonProps={cancelButtonProps || {}}
              cancelButtonProps={cancelButtonProps || {}}
              hasActionButton={hasActionButton}
              hasCancelButton={hasCancelButton}
              inDialog
            />
          </DialogFooter>
        )}
      </DialogContent>
    </DialogPrimitive.Root>
  ),
);
Dialog.displayName = DialogPrimitive.Root.displayName;

// const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-[1000] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    ref={ref}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  /**
   * Whether to show the 'X' close button in the top right corner.
   * @default true
   */
  hasCloseButton?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hasCloseButton = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      className={cn(
        "fixed left-[50%] top-[50%] z-[1000] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}

      {hasCloseButton && (
        <DialogPrimitive.Close className="data-[state=open]:bg-accent absolute right-2.5 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:text-muted-foreground">
          <Icon icon="close" size="sm" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    ref={ref}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    className={cn("text-sm text-muted-foreground", className)}
    ref={ref}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  // DialogDescription,
  // DialogFooter,
  // DialogHeader,
  // DialogOverlay,
  // DialogPortal,
  // DialogTitle,
  // DialogTrigger,
  Dialog,
  DialogClose,
  DialogContent,
  type DialogProps,
};
